
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { debugLogger } from '@/utils/debugLogger';
import { useAuth } from "@/hooks/useAuth";
import { StaticImageBackground } from "@/components/auth/StaticImageBackground";
import AuthErrorState from "@/components/auth/AuthErrorState";
import AuthLoadingState from "@/components/auth/AuthLoadingState";
import AuthForm from "@/components/auth/AuthForm";
import InterlacedLogoV2 from "@/components/ui/InterlacedLogoV2";

const LoginPage = () => {
  const { user, loading: authLoading, error: authError, retry } = useAuth();
  const navigate = useNavigate();

  debugLogger.debug('LoginPage - Render:', { 
    hasUser: !!user,
    authLoading,
    authError,
    currentPath: window.location.pathname
  });

  const getRedirectDestination = async (userId: string): Promise<string> => {
    try {
      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('onboarding_completed')
        .eq('id', userId)
        .single();

      if (error) {
        debugLogger.warn('LoginPage - Could not fetch profile data:', error);
        return '/main/onboarding';
      }

      const hasCompletedOnboarding = profileData?.onboarding_completed || false;
      return hasCompletedOnboarding ? '/main/feed' : '/main/onboarding';
    } catch (error) {
      debugLogger.error('LoginPage - Error checking onboarding status:', error);
      return '/main/onboarding';
    }
  };

  useEffect(() => {
    if (user && !authLoading) {
      debugLogger.debug('LoginPage: User authenticated, redirecting...');
      
      getRedirectDestination(user.id).then((destination) => {
        debugLogger.debug('LoginPage: Redirecting to:', destination);
        navigate(destination, { replace: true });
      });
    }
  }, [user, authLoading, navigate]);

  const handleLearnMore = () => {
    navigate('/#como-funciona');
  };

  if (authError) {
    debugLogger.debug('LoginPage - Showing auth error state:', authError);
    return <AuthErrorState error={authError} onRetry={retry} />;
  }

  if (authLoading && !user) {
    debugLogger.debug('LoginPage - Showing loading state');
    return <AuthLoadingState onRetry={retry} />;
  }

  return (
    <div className="relative w-full min-h-screen flex items-center justify-center overflow-hidden bg-black safe-area-all">
      <StaticImageBackground />

      <div className="relative z-10 w-full max-w-md px-6 py-12 backdrop-blur-sm bg-black bg-opacity-40 rounded-2xl border border-white/10 shadow-xl animate-fade-in mx-[15px]">
        <div className="text-center mb-10">
          <InterlacedLogoV2 variant="horizontal" size="lg" as="h1" className="mb-2 mx-auto" />
          <p className="text-white/70">Conexões exclusivas para pessoas exigentes</p>
        </div>

        <AuthForm onLearnMore={handleLearnMore} />
      </div>
    </div>
  );
};

export default LoginPage;
